import {cdnImage} from '@/util/cdnImage';
import MUIBox from '@/components/MUI-Components/MUI-Box';
import MUITypography from '@/components/MUI-Components/MUI-Typography';
import MUIButton from '@/components/MUI-Components/MUI-Button';
import MUIContainer from '@/components/MUI-Components/MUI-Container';
import React, {memo} from 'react';
import DisplayImage from '../display-image';
const Arrow = cdnImage('Blockchain-images/arrow.webp');
import parse from 'html-react-parser';
import {borderRadius, colors, fontSize, lineHeight} from '@/styles/tokens';

// Migrated from globals.scss `.webdev-background` (+ tabScreen). Single-owner, Lets-discuss's
// own hardcoded base classNames — sole consumer confirmed via ownership-report + repo-wide grep.
const webdevBackgroundSx = {
    borderRadius: '23px', // no exact token match (closest are 20px/24px) — preserved for parity
    backgroundImage: 'linear-gradient(270deg, rgba(35, 92, 209, 1) 0%, rgba(23, 51, 110, 1) 100%)', // no token match — preserved for parity
    padding: '30px 50px',
    position: 'relative',
    '@media (max-width: 768px)': {
        padding: '30px !important' // no exact token match — preserved for parity
    }
} as const;

// Migrated from globals.scss `.lets-descuss` (+ laptopScreen/tabScreen/mobileScreen). The nested
// `.spectrum-header-wrapper .spectrum-heading`, `.solution-header-wrapper .solution-sub-heading`,
// and `.header-contact-btn .nav-button` selectors look shared (those base classes are used by
// 100+ other components/pages), but the compiled rule is scoped by this `.lets-descuss` ancestor
// and can only ever match inside this component's own render tree — genuinely single-owner.
// `.spectrum-heading`/`.solution-sub-heading`/`.header-contact-btn`/`.nav-button`/`.animated-button`
// base rules stay global, untouched — out of scope for this component-only pass.
const letsDescussSx = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    '& .spectrum-header-wrapper .spectrum-heading': {
        fontSize: `${fontSize[28]} !important`,
        color: `${colors.text.inverse} !important`
    },
    '& .solution-header-wrapper .solution-sub-heading': {
        fontSize: `${fontSize[18]} !important`,
        color: `${colors.text.inverse} !important`
    },
    '& .header-contact-btn .nav-button': {
        backgroundColor: 'rgba(236, 206, 17, 1) !important', // no token match — preserved for parity
        color: `${colors.text.primary} !important`
    },
    '& .lets-discuss-hover': {
        borderRadius: `${borderRadius.lg} !important`
    },
    '& .lets-discuss-hover:hover': {
        backgroundColor: 'transparent !important',
        border: '1px solid rgba(236, 206, 17, 1) !important', // no token match — preserved for parity
        color: 'rgba(236, 206, 17, 1) !important' // no token match — preserved for parity
    },
    '@media (max-width: 1024px)': {
        flexDirection: 'column !important',
        alignItems: 'flex-start !important',
        '& .spectrum-header-wrapper .spectrum-heading': {
            fontSize: `${fontSize[32]} !important`,
            color: `${colors.text.inverse} !important`
        },
        '& .solution-header-wrapper .solution-sub-heading': {
            fontSize: `${fontSize[20]} !important`,
            color: `${colors.text.inverse} !important`,
            padding: '15px 0 !important',
            marginTop: '0 !important'
        }
    },
    '@media (max-width: 768px)': {
        flexDirection: 'column !important',
        rowGap: '20px !important',
        alignItems: 'flex-start !important',
        '& .solution-header-wrapper .spectrum-heading': {
            lineHeight: '55px !important' // no exact token match (closest are 48px/56px) — preserved for parity
        },
        '& .solution-header-wrapper .solution-sub-heading': {
            fontSize: `${fontSize[20]} !important`
        }
    },
    '@media (max-width: 430px)': {
        '& .spectrum-header-wrapper .spectrum-heading': {
            fontSize: '26px !important', // no exact token match (closest are 24px/28px) — preserved for parity
            color: `${colors.text.inverse} !important`
        },
        '& .solution-header-wrapper .spectrum-heading': {
            lineHeight: `${lineHeight[40]} !important`
        },
        '& .solution-header-wrapper .solution-sub-heading': {
            padding: '0 !important',
            marginBlockEnd: '0 !important'
        }
    }
} as const;

// Migrated from globals.scss `.lets-discuss-inner` (+ 1441px min-width/laptopScreen/tabScreen).
// Applies to the heading wrapper Box (carries `spectrum-header-wrapper solution-header-wrapper
// solutions left lets-discuss-inner`) — single-owner, sole consumer this component.
const headingWrapperSx = {
    maxWidth: '660px',
    '@media (min-width: 1441px)': {
        maxWidth: '900px !important'
    },
    '@media (max-width: 1024px)': {
        maxWidth: '100% !important'
    },
    '@media (max-width: 768px)': {
        maxWidth: 'none !important'
    }
} as const;

// Migrated from globals.scss `.spriell-image` (+ laptopScreen/ipadScreen/tabScreen). The
// ipadScreen (≤992px) and tabScreen (≤768px) `display: none` overrides duplicated the
// laptopScreen (≤1024px) value with no behavior change — collapsed into one breakpoint here,
// same precedent as `.banner-main-outer` elsewhere in globals.scss. Merged with the page-level
// `imageSx` prop (4 consumers already pass this) via a flat spread — page values always win.
const spriellImageBaseSx = {
    position: 'absolute',
    right: '280px',
    top: '0px',
    transform: 'rotateZ(-17deg)',
    '@media (max-width: 1024px)': {
        display: 'none !important'
    }
} as const;

// Migrated from globals.scss `.header-contact-btn .lets-discuss-hover` (mobileScreen only).
// `.header-contact-btn` is shared elsewhere, but `.lets-discuss-hover` is exclusive to this
// component's own CTA button, so the compiled rule can only ever match here.
const headerContactBtnSx = {
    '@media (max-width: 430px)': {
        '& .lets-discuss-hover': {
            padding: '15px 20px !important',
            fontSize: `${fontSize[15]} !important`
        }
    }
} as const;

const LetDiscuss = (props: any) => {
    const {heading, subheading, btnLabel, openForm, className, imageSx} = props;
    return (
        <MUIContainer>
            <MUIBox className="webdev-background margin" sx={webdevBackgroundSx}>
                <MUIBox className="lets-descuss" sx={letsDescussSx}>
                    <MUIBox
                        className="spectrum-header-wrapper solution-header-wrapper solutions left lets-discuss-inner"
                        sx={headingWrapperSx}
                    >
                        <MUITypography className="spectrum-heading color-black ">{heading}</MUITypography>
                        {subheading && <p className="solution-sub-heading color-black data_links">{subheading ? parse(subheading) : ''}</p>}
                    </MUIBox>
                    <MUIBox className={`spriell-image ${className || ''}`} sx={{...spriellImageBaseSx, ...imageSx}}>
                        <DisplayImage src={Arrow} alt="arrow image" priority={false} />
                    </MUIBox>
                    <MUIBox className="header-contact-btn" sx={headerContactBtnSx}>
                        <MUIButton
                            className="nav-button lets-discuss-hover animated-button"
                            variant="contained"
                            label={btnLabel}
                            onClick={openForm}
                        />
                    </MUIBox>
                </MUIBox>
            </MUIBox>
        </MUIContainer>
    );
};

export default memo(LetDiscuss);
